import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { IUsageForm } from "@/pages/products/usage/usageModal";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface ProductUsageOverview {
  id: number;
  external_id: string;
  name?: string;
  category: string;
  utilities_used: boolean | null;
  consumables_used: boolean | null;
  lifespan: string | null;
  electricity_amount: number | null;
  consumables_count: number | null;
  status: "complete" | "incomplete";
}

export function useGetProductUsageOverview({
  pagination,
  filter,
  search,
  sort,
  year,
  showUnrelated = false,
  enabled,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  year?: number;
  showUnrelated?: boolean;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["usage", "list", showUnrelated, year, pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<ProductUsageOverview>>("/products/usage", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            year,
            show_unrelated: showUnrelated,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export function usePostUsage() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productIds, usage }: { productIds: number[]; usage: IUsageForm }) =>
      axios.post("/products/usage", usage, {
        params: {
          ids: productIds.join(";"),
        },
      }),
    onSuccess: (_) => {
      smartQueryClient.invalidateQueries(["usage"]);
      smartQueryClient.invalidateQueries(["dashboard"]);
    },
  });
}

interface IGetUsageForm extends IUsageForm {
  proxy_id?: number | null;
}

export function useGetUsage({ productId, enabled }: { productId: number; enabled?: boolean }) {
  return useQuery({
    queryKey: ["usage", productId],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios.get<IGetUsageForm>("/products/" + productId + "/usage").then((result) => result.data),
  });
}

export function usePatchUsage() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ productId, usage }: { productId: number; usage: Partial<IUsageForm> }) =>
      axios.patch("/products/usage", usage, {
        params: {
          product_id: productId,
        },
      }),
    onSuccess: (_) => {
      smartQueryClient.invalidateQueries(["consumables"]);
      smartQueryClient.invalidateQueries(["usage", "status"]);
      smartQueryClient.invalidateQueries(["usage", "list"]);
      smartQueryClient.invalidateQueries(["dashboard"]);
    },
  });
}

export function useGetUsageStatus({
  productId,
  enabled,
}: {
  productId: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["usage", "status", productId],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios.get<IGetUsageForm>("/products/" + productId + "/usage").then((result) => result.data),
  });
}
